import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { Clipboard, Clock, ChefHat, LayoutGrid } from 'lucide-react';
import MisPedidosTab from '../components/pedidos/MisPedidosTab';
import PedidosEntrantesTab from '../components/pedidos/PedidosEntrantesTab';
import EnCocinaTab from '../components/pedidos/EnCocinaTab';
import GestionCompletaTab from '../components/pedidos/GestionCompletaTab';
import AyudaContextual from '../components/AyudaContextual';

type TabId = 'mis-pedidos' | 'entrantes' | 'en-cocina' | 'gestion-completa';

interface Tab {
  id: TabId;
  label: string;
  icon: React.ReactNode;
  rolesPermitidos: string[];
  component: React.ReactNode;
}

/**
 * Página principal del módulo de Pedidos con navegación por tabs
 * basada en el rol del usuario
 */
const PedidosMain: React.FC = () => {
  const { user } = useAuth();

  // Determinar tab inicial según el rol
  const getTabInicial = (): TabId => {
    if (user?.rol === 'administrador' || user?.rol === 'chef') {
      return 'entrantes'; // Admin y Chef ven "Pedidos Entrantes" por defecto
    }
    if (user?.rol === 'cocina') {
      return 'en-cocina'; // Cocina ve "En Cocina" por defecto
    }
    return 'mis-pedidos'; // Clientes ven "Mis Pedidos" por defecto
  };

  const [tabActiva, setTabActiva] = useState<TabId>(getTabInicial());

  // Definir todas las tabs disponibles
  const todasLasTabs: Tab[] = [
    {
      id: 'mis-pedidos',
      label: 'Mis Pedidos',
      icon: <Clipboard className="w-4 h-4" />,
      rolesPermitidos: ['cliente', 'administrador'],
      component: <MisPedidosTab />,
    },
    {
      id: 'entrantes',
      label: 'Pedidos Entrantes',
      icon: <Clock className="w-4 h-4" />,
      rolesPermitidos: ['administrador', 'chef'],
      component: <PedidosEntrantesTab />,
    },
    {
      id: 'en-cocina',
      label: 'En Cocina',
      icon: <ChefHat className="w-4 h-4" />,
      rolesPermitidos: ['cocina', 'chef', 'administrador'],
      component: <EnCocinaTab />,
    },
    {
      id: 'gestion-completa',
      label: 'Gestión Completa',
      icon: <LayoutGrid className="w-4 h-4" />,
      rolesPermitidos: ['administrador'],
      component: <GestionCompletaTab />,
    },
  ];

  // Filtrar tabs según el rol del usuario
  const tabsDisponibles = todasLasTabs.filter((tab) =>
    tab.rolesPermitidos.includes(user?.rol || '')
  );

  // Establecer tab inicial basada en rol
  React.useEffect(() => {
    if (tabsDisponibles.length > 0) {
      // Si la tab activa no está disponible, cambiar a la primera disponible
      const tabActualDisponible = tabsDisponibles.find(
        (t) => t.id === tabActiva
      );
      if (!tabActualDisponible) {
        setTabActiva(tabsDisponibles[0].id);
      }
    }
  }, [user?.rol, tabsDisponibles, tabActiva]);

  // Obtener el componente de la tab activa
  const tabActualComponent = tabsDisponibles.find(
    (t) => t.id === tabActiva
  )?.component;

  if (!user) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-gray-500 dark:text-gray-400">
          Debes iniciar sesión para ver esta página
        </p>
      </div>
    );
  }

  if (tabsDisponibles.length === 0) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-gray-500 dark:text-gray-400">
          No tienes permisos para acceder a este módulo
        </p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 max-w-7xl">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Gestión de Pedidos
          </h1>
          <AyudaContextual seccion="gestion-pedidos" />
        </div>
        <p className="text-gray-600 dark:text-gray-400">
          Administra y consulta los pedidos del sistema
        </p>
      </div>

      {/* Tabs Navigation */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="flex space-x-4" aria-label="Tabs">
          {tabsDisponibles.map((tab) => {
            const isActive = tab.id === tabActiva;
            return (
              <button
                key={tab.id}
                onClick={() => setTabActiva(tab.id)}
                className={`
                  flex items-center gap-2 px-4 py-3 text-sm font-medium
                  border-b-2 transition-colors duration-200
                  ${
                    isActive
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:border-gray-600'
                  }
                `}
                aria-current={isActive ? 'page' : undefined}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        {tabActualComponent}
      </div>
    </div>
  );
};

export default PedidosMain;
